/**
 * Pure logic of the store dialog (port of the AddStoreComponent form): flat
 * form values <-> nested store record, validation, and the normalisation
 * applied on submit.
 */
import {
  ALLOWED_EXCLUSIVE_SYMBOLS,
  type ExclusiveSymbolKey,
} from '@/shared/constants';
import type { OptionalStoreKeyboardKey, StoreInput, StoreRecord } from './api';
import { EMAIL_PATTERN } from '@/shared/lib/validation';

export const COUNTRIES = [
  'Australia',
  'Austria',
  'Azerbaijan',
  'Bahrain',
  'Belgium',
  'Brazil',
  'Canada',
  'China',
  'Czech Republic',
  'France',
  'Germany',
  'Greece',
  'Guam',
  'Hong Kong',
  'India',
  'Indonesia',
  'Italy',
  'Japan',
  'Kazakhstan',
  'Korea',
  'Kuwait',
  'Luxembourg',
  'Macau',
  'Malaysia',
  'Mexico',
  'Morocco',
  'Netherlands',
  'New Zealand',
  'Northern Mariana Islands',
  'Panama',
  'Poland',
  'Qatar',
  'Russian Federation',
  'Saudi Arabia',
  'Singapore',
  'Spain',
  'Sweden',
  'Switzerland',
  'Taiwan',
  'Thailand',
  'Turkey',
  'Ukraine',
  'United Arab Emirates',
  'United Kingdom',
  'United States',
  'Vietnam',
].sort((a, b) => a.localeCompare(b));

export const REGIONS = ['EMEA', 'APAC', 'CHINA', 'US', 'JAPAN'].sort((a, b) =>
  a.localeCompare(b),
);

export const KEYBOARDS: readonly {
  key: OptionalStoreKeyboardKey;
  label: string;
}[] = [
  { key: 'arabic', label: 'Arabic' },
  { key: 'chinese', label: 'Simplified Chinese' },
  { key: 'chineseTw', label: 'Traditional Chinese (Taiwan)' },
  { key: 'chineseHk', label: 'Traditional Chinese (Hong Kong)' },
  { key: 'cyrillic', label: 'Cyrillic' },
];

export type KeyboardKey = OptionalStoreKeyboardKey;

export interface StoreFormValues {
  storeCode: string;
  name: string;
  fullAddress: string;
  email: string;
  phone: string;
  currency: string;
  country: string;
  region: string;
  workshopSav: string;
  workshopEvent: string;
  /** ISO strings, or '' when unset. */
  workshopEventStart: string;
  workshopEventEnd: string;
  stockControl: boolean;
  bypassHeadOfficeApproval: boolean;
  bypassBoutiqueManagerApproval: boolean;
  permanentInStoreWorkshop: boolean;
  disabled: boolean;
  centralFlowForbidden: boolean;
  abcdiorActive: boolean;
  dmsActive: boolean;
  iosKeyboardActive: boolean;
  keyboards: Record<KeyboardKey, boolean>;
  symbolActive: boolean;
  exclusiveSymbolActive: boolean;
  exclusiveSymbols: Record<ExclusiveSymbolKey, boolean>;
  iconicThreadsActive: boolean;
  exclusiveThreadsActive: boolean;
}

const allFalse = <K extends string>(keys: readonly K[]): Record<K, boolean> =>
  Object.fromEntries(keys.map((key) => [key, false])) as Record<K, boolean>;

export const emptyStoreForm = (): StoreFormValues => ({
  storeCode: '',
  name: '',
  fullAddress: '',
  email: '',
  phone: '',
  currency: '',
  country: '',
  region: '',
  workshopSav: '',
  workshopEvent: '',
  workshopEventStart: '',
  workshopEventEnd: '',
  stockControl: false,
  bypassHeadOfficeApproval: false,
  bypassBoutiqueManagerApproval: false,
  permanentInStoreWorkshop: false,
  disabled: false,
  centralFlowForbidden: false,
  abcdiorActive: false,
  dmsActive: false,
  iosKeyboardActive: false,
  keyboards: allFalse(KEYBOARDS.map((k) => k.key)),
  symbolActive: false,
  exclusiveSymbolActive: false,
  exclusiveSymbols: allFalse(ALLOWED_EXCLUSIVE_SYMBOLS.map((s) => s.key)),
  iconicThreadsActive: false,
  exclusiveThreadsActive: false,
});

/** Nested record -> flat form. */
export const storeToForm = (store: StoreRecord): StoreFormValues => {
  const empty = emptyStoreForm();
  const keyboard = store.iosKeyboard?.keyboard ?? {};
  const symbols = store.exclusiveSymbolActive?.symbols ?? {};
  return {
    ...empty,
    storeCode: store.storeCode ?? '',
    name: store.name ?? '',
    fullAddress: store.fullAddress ?? '',
    email: store.email ?? '',
    phone: store.phone ?? '',
    currency: store.currency ?? '',
    country: store.country ?? '',
    region: store.region ?? '',
    workshopSav: store.workshopSavId ?? '',
    workshopEvent: store.workshopEventId ?? '',
    workshopEventStart: store.workshopEventStart ?? '',
    workshopEventEnd: store.workshopEventEnd ?? '',
    stockControl: store.stockControl ?? false,
    bypassHeadOfficeApproval: store.bypassHeadOfficeApproval ?? false,
    bypassBoutiqueManagerApproval: store.bypassBoutiqueManagerApproval ?? false,
    permanentInStoreWorkshop: store.permanentInStoreWorkshop ?? false,
    disabled: store.disabled ?? false,
    centralFlowForbidden: store.centralFlowForbidden ?? false,
    abcdiorActive: store.frontOfficeAccess?.abcdior ?? false,
    dmsActive: store.frontOfficeAccess?.dms ?? false,
    iosKeyboardActive: store.iosKeyboard?.active ?? false,
    keyboards: Object.fromEntries(
      KEYBOARDS.map((k) => [k.key, keyboard[k.key] ?? false]),
    ) as Record<KeyboardKey, boolean>,
    symbolActive: store.symbolActive ?? false,
    exclusiveSymbolActive: store.exclusiveSymbolActive?.active ?? false,
    exclusiveSymbols: Object.fromEntries(
      ALLOWED_EXCLUSIVE_SYMBOLS.map((s) => [s.key, symbols[s.key] ?? false]),
    ) as Record<ExclusiveSymbolKey, boolean>,
    iconicThreadsActive: store.iconicThreadsActive ?? false,
    exclusiveThreadsActive: store.exclusiveThreadsActive ?? false,
  };
};

export type StoreFormErrors = Partial<
  Record<
    'storeCode' | 'name' | 'fullAddress' | 'email' | 'phone' | 'workshopSav',
    string
  >
>;

export const validateStoreForm = (values: StoreFormValues): StoreFormErrors => {
  const errors: StoreFormErrors = {};
  if (!values.storeCode || values.storeCode.length > 100)
    errors.storeCode = 'Wrong store code';
  if (!values.name || values.name.length > 100) errors.name = 'Wrong name';
  if (!values.fullAddress || values.fullAddress.length > 500)
    errors.fullAddress = 'Wrong address';
  if (
    !values.email ||
    values.email.length > 100 ||
    !EMAIL_PATTERN.test(values.email)
  )
    errors.email = 'Wrong email';
  if (values.phone.length > 30) errors.phone = 'Wrong phone';
  // Workshop SAV becomes mandatory when the central flow is forbidden.
  if (values.centralFlowForbidden && !values.workshopSav)
    errors.workshopSav = 'Wrong workshop SAV';
  return errors;
};

/** Event range of a permanent in-store workshop. */
export const PERMANENT_EVENT_START = new Date('2019').toISOString();
export const PERMANENT_EVENT_END = new Date('2100').toISOString();

/** `{ [key + suffix]: isOn(key) }` for every key: the flag maps of the payload. */
function flagsOf<K extends string, S extends string>(
  keys: readonly K[],
  suffix: S,
  isOn: (key: K) => boolean,
): Record<`${K}${S}`, boolean>;
function flagsOf(
  keys: readonly string[],
  suffix: string,
  isOn: (key: string) => boolean,
): Record<string, boolean> {
  return Object.fromEntries(
    keys.map((key): [string, boolean] => [`${key}${suffix}`, isOn(key)]),
  );
}

const KEYBOARD_KEYS = KEYBOARDS.map((k) => k.key);
const SYMBOL_KEYS = ALLOWED_EXCLUSIVE_SYMBOLS.map((s) => s.key);

/** What is sent to the API. */
export const formToStore = (values: StoreFormValues): StoreInput => {
  const permanent = values.permanentInStoreWorkshop;
  const keyboardsOn = values.iosKeyboardActive;
  const symbolsOn = values.exclusiveSymbolActive;
  const orNull = (value: string) => (value === '' ? null : value);
  const keyboardOn = (key: KeyboardKey) => keyboardsOn && values.keyboards[key];
  const symbolOn = (key: ExclusiveSymbolKey) =>
    symbolsOn && values.exclusiveSymbols[key];
  return {
    name: values.name,
    email: values.email,
    fullAddress: values.fullAddress,
    phone: values.phone,
    storeCode: values.storeCode,
    currency: orNull(values.currency),
    workshopSav: orNull(values.workshopSav),
    workshopEvent: orNull(values.workshopEvent),
    workshopEventStart: permanent
      ? PERMANENT_EVENT_START
      : orNull(values.workshopEventStart),
    workshopEventEnd: permanent
      ? PERMANENT_EVENT_END
      : orNull(values.workshopEventEnd),
    country: orNull(values.country),
    region: orNull(values.region),
    stockControl: values.stockControl,
    bypassHeadOfficeApproval: values.bypassHeadOfficeApproval,
    bypassBoutiqueManagerApproval: values.bypassBoutiqueManagerApproval,
    permanentInStoreWorkshop: permanent,
    disabled: values.disabled,
    centralFlowForbidden: values.centralFlowForbidden,
    abcdiorActive: values.abcdiorActive,
    dmsActive: values.dmsActive,
    iosKeyboardActive: keyboardsOn,
    ...flagsOf(KEYBOARD_KEYS, 'KeyboardActive', keyboardOn),
    symbolActive: values.symbolActive,
    exclusiveSymbolActive: {
      active: symbolsOn,
      symbols: flagsOf(SYMBOL_KEYS, '', symbolOn),
    },
    // The `id` of every allowed symbol is `${key}ExclusiveSymbolActive`.
    ...flagsOf(SYMBOL_KEYS, 'ExclusiveSymbolActive', symbolOn),
    iconicThreadsActive: values.iconicThreadsActive,
    exclusiveThreadsActive: values.exclusiveThreadsActive,
    workshopSavId: orNull(values.workshopSav),
    workshopEventId: orNull(values.workshopEvent),
    frontOfficeAccess: { abcdior: values.abcdiorActive, dms: values.dmsActive },
    iosKeyboard: {
      active: keyboardsOn,
      keyboard: { latin: true, ...flagsOf(KEYBOARD_KEYS, '', keyboardOn) },
    },
  };
};

/** Value for a `datetime-local` input from an ISO string (local time, minute precision). */
export const toDateTimeLocal = (iso: string): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** ISO string from a `datetime-local` value, '' when empty. */
export const fromDateTimeLocal = (value: string): string =>
  value ? new Date(value).toISOString() : '';
